import React, { useState } from "react";
import { ShieldCheck, KeyRound, Lock, Mail, UserPlus } from "lucide-react";
import { login, register, errText } from "../api";
import { useAuth } from "../auth/AuthContext";
import { toast } from "sonner";

export default function Login() {
  const { signIn } = useAuth();
  const [tab, setTab] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [otp, setOtp] = useState("");
  const [needs2fa, setNeeds2fa] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (tab === "register") {
        await register({ email, password, name });
        toast.success("Registrazione inviata: in attesa di approvazione admin");
        setTab("login");
        return;
      }
      const res = await login({ email, password, otp: needs2fa ? otp : undefined });
      if (res.requires2fa) {
        setNeeds2fa(true);
        toast.info("Inserisci il codice a 6 cifre dall'app authenticator");
        return;
      }
      signIn(res.token, res.user);
      toast.success(`Benvenuto, ${res.user.name}`);
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grain grid place-items-center p-4 relative z-10">
      <div className="w-full max-w-[400px] float-in">
        <div className="flex items-center gap-3 mb-6 justify-center">
          <div className="w-12 h-12 rounded-2xl grid place-items-center bg-gradient-to-br from-amber-400 via-rose-500 to-sky-500 text-[#0B0E17] font-black text-sm shadow-[0_0_28px_rgba(245,158,11,0.4)]">
            CAV
          </div>
          <div>
            <div className="font-heading font-extrabold text-lg leading-none">
              <span className="gold-text">C.A.V</span> · Condannati a Vincere
            </div>
            <div className="text-[10px] text-slate-400 tracking-widest uppercase mt-1">MT5 XAUUSD · Trading Control</div>
          </div>
        </div>

        <div className="flex gap-1 bg-[#0F1728] border border-white/10 rounded-lg p-1 mb-4">
          {[["login", "Accedi"], ["register", "Registrati (Trader)"]].map(([id, label]) => (
            <button key={id} data-testid={`auth-tab-${id}`}
              onClick={() => { setTab(id); setNeeds2fa(false); }}
              className={`flex-1 py-2 rounded-md text-[11px] font-semibold transition-colors ${
                tab === id ? "bg-amber-500/20 text-amber-400" : "text-slate-400 hover:text-slate-200"
              }`}>
              {label}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="panel p-6 space-y-4">
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
            {tab === "register" ? <UserPlus size={13} className="text-amber-400" /> : <Lock size={13} className="text-amber-400" />}
            {tab === "register" ? "Nuovo Trader" : "Accesso Operatore"}
          </div>

          {!needs2fa ? (
            <>
              {tab === "register" && (
                <Field testid="register-name-input" placeholder="nome" value={name} onChange={(e) => setName(e.target.value)} />
              )}
              <Field icon={<Mail size={15} />} testid="login-email-input" type="email" placeholder="email operatore"
                value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
              <Field icon={<KeyRound size={15} />} testid="login-password-input" type="password" placeholder="password"
                value={password} onChange={(e) => setPassword(e.target.value)} />
            </>
          ) : (
            <div className="space-y-2">
              <div className="text-[11px] text-slate-300 flex items-center gap-2">
                <ShieldCheck size={14} className="text-emerald-400" /> Verifica in due passaggi (2FA)
              </div>
              <Field testid="login-otp-input" placeholder="codice a 6 cifre" value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} autoFocus
                inputMode="numeric" mono />
            </div>
          )}

          <button type="submit" data-testid={tab === "register" ? "register-submit-button" : "login-submit-button"}
            disabled={busy}
            className="w-full py-2.5 rounded-lg text-[13px] font-bold text-[#0B0E17] bg-gradient-to-br from-amber-400 to-amber-500 hover:from-amber-300 hover:to-amber-400 transition-colors disabled:opacity-60">
            {busy ? "Attendere…" : tab === "register" ? "Invia registrazione" : needs2fa ? "Verifica codice" : "Accedi"}
          </button>

          {tab === "login" && (
            <div className="rounded-lg bg-white/[0.03] border border-white/8 p-3 text-[10px] text-slate-400 leading-relaxed">
              <b className="text-slate-300">Account demo:</b><br />
              admin@mt5.local / admin123 (admin)<br />
              trader@mt5.local / trader123 (trader attivo)<br />
              viewer@mt5.local / viewer123 (sola lettura)
            </div>
          )}
          {tab === "register" && (
            <p className="text-[10px] text-slate-500 leading-relaxed">
              I nuovi trader restano <b className="text-amber-400">in attesa</b> finché un admin non li approva. Dopo
              l'approvazione dovrai acquistare una licenza di accesso (10€/giorno).
            </p>
          )}
        </form>
      </div>
    </div>
  );
}

function Field({ icon, mono, testid, ...props }) {
  return (
    <div className="relative">
      {icon && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">{icon}</span>}
      <input
        data-testid={testid}
        {...props}
        className={`w-full bg-[#0A1220] border border-white/12 rounded-lg py-2.5 ${icon ? "pl-9" : "pl-3"} pr-3 text-[13px] text-slate-100 outline-none focus:border-amber-500/50 ${mono ? "font-mono tracking-[0.3em] text-center" : ""}`}
      />
    </div>
  );
}
